import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { apiFetch } from '@/lib/api';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

/** `/settings/*` 共通レイアウトフレーム (v0.2.5 S-11、T-1101)。
 *  設計: docs/design/v025-store-node-hierarchy.md「コンフィグ画面」節、docs/design/v025-config-ui.md。
 *  タブはフィーチャーフラグで動的に表示/非表示 (hierarchy_management OFF → 組織階層系タブを隠す)。
 *  v0.3 (S-0、来客数管理タブ追加の前提): 12タブ目追加で375pxモバイル幅の発見性が破綻するため、
 *  グループ (2段タブ) 化した。設計: docs/design/v03-visitor-count-generalization.md §5-0 */

type TenantFeature = { feature_code: string; is_enabled: boolean };

// v0.4 追加要件⑱ D-443: 'hr' が14タブ (全体の45%) に肥大し「シフト運用設定」の受け皿に
// なっていたため 'hr' (人事・勤怠) と 'shift' (シフト設定) に分割した。
// ⚠️ 'data' は 'system' へ吸収しない — Lite では liteOnly の3タブが加わり5タブになるため、
//    Full だけを見て統合すると Lite 側の分類が壊れる (§6e)。
type SettingsGroup = 'organization' | 'data' | 'hr' | 'shift' | 'system';

const GROUP_LABELS: Record<SettingsGroup, string> = {
  organization: '組織・権限',
  data: 'データ設定',
  hr: '人事・勤怠',
  shift: 'シフト設定',
  system: 'システム設定',
};
const GROUP_ORDER: SettingsGroup[] = ['organization', 'data', 'hr', 'shift', 'system'];

type SettingsTab = {
  to: string;
  label: string;
  group: SettingsGroup;
  /** 指定時、このフィーチャーフラグが有効な場合のみ表示する */
  requiresFeature?: string;
  /** v0.3 (S-16): 指定時、effective_permissionsでこのfeature+actionを保有する場合のみ表示する
   *  (requiresFeatureと併用可、両方の条件をANDで満たす必要がある)。未指定のタブは
   *  従来通り会社管理者以上のみ (下のisAdminフォールバック)。 */
  requiredPermission?: { feature: string; action: 'view' | 'manage' };
  /** v0.4 TD-22: バックエンドが GET も含めて全店舗スコープを要求するタブに付ける。
   *  付けないと own_store の店長にタブだけ見えて開くと 403 になる dead-end になる。 */
  requiresTenantWideScope?: boolean;
  /** v1.0 Phase1-5最終監査 C-4 (docs/design/v10-phase1-5-final-audit-triage.md): trueの場合
   *  edition==='lite'テナントでは非表示にする (AppLayout.tsxのfullOnlyと同じ意味論)。 */
  fullOnly?: boolean;
  /** fullOnlyの逆。trueの場合edition==='full'テナントでは非表示にする。 */
  liteOnly?: boolean;
};

const TABS: SettingsTab[] = [
  { to: '/settings/general', label: '一般', group: 'system' },
  { to: '/settings/hierarchy', label: '組織階層', group: 'organization', requiresFeature: 'hierarchy_management' },
  { to: '/settings/node-types', label: '階層レベル定義', group: 'organization', requiresFeature: 'hierarchy_management' },
  // v0.3 (S-16): custom_rolesフラグ + role:manage permission の両方を満たす場合のみ表示
  { to: '/settings/roles', label: 'ロール管理', group: 'organization', requiresFeature: 'custom_roles', requiredPermission: { feature: 'role', action: 'manage' } },
  { to: '/settings/approvals', label: '承認ワークフロー', group: 'organization', requiresFeature: 'approval_workflow', requiredPermission: { feature: 'approval_workflow', action: 'manage' } },
  // v0.4 章② (D-57/D-58): 条件集合3層 + 階層継承カタログ。Full 限定 (D-1)
  { to: '/settings/conditions', label: '条件集合', group: 'organization', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'view' } },
  { to: '/settings/inheritance', label: '階層継承設定', group: 'organization', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'view' } },
  { to: '/settings/aggregation', label: '集計設定', group: 'data' },
  // v1.0 (Phase 3 Sprint 9): Lite版データモデルのマスタ管理。
  // v1.0 Phase1-5最終監査 C-4で是正: liteOnly未設定のままFull版に表示されるとaccount_categories等
  // テーブル自体が存在せず500になっていた (バックエンド側は routes/account-categories.ts 等に
  // edition guardを追加済み、フロント側もタブ非表示で揃える)。requiredPermissionも追加 (platform-reviewer
  // 指摘: これが無いと isAdmin のみでゲートされ続け、custom_roles で import:manage を付与された
  // ユーザーがバックエンドは通るのにUIで弾かれる、C-4の本来の目的が未達成のままだった)
  { to: '/settings/account-categories', label: '勘定科目マスタ', group: 'data', liteOnly: true, requiredPermission: { feature: 'import', action: 'manage' } },
  { to: '/settings/suppliers-lite', label: '仕入先マスタ', group: 'data', liteOnly: true, requiredPermission: { feature: 'import', action: 'manage' } },
  // v1.0 (Phase 3 Sprint 10): Lite版CSV取込のマッピング設定
  { to: '/settings/csv-column-mappings', label: 'CSV列名マッピング', group: 'data', liteOnly: true, requiredPermission: { feature: 'import', action: 'manage' } },
  // v0.3 (S-5): 来客数管理 (docs/design/v03-visitor-count-generalization.md §5-1)
  { to: '/settings/visitor-count', label: '来客数管理', group: 'data' },
  // v0.5⑧ 経営会議PDCA (D-901、§7-5): データ締切マスタ。requiresFeature を必ず付ける
  // (無いとフラグOFFでもタブが出て403になる、4巡目 design-expert M-2)
  // 消費税率 (docs/design/v05-consumption-tax-rate-and-target-tax-basis.md §3-3)。Full 限定。
  // GET は tenant_config:view (scope 不問)、書込みのみ all_stores 限定 (画面内で出し分け)
  { to: '/settings/consumption-tax-rates', label: '消費税率', group: 'data', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'view' } },
  { to: '/settings/data-closing-deadlines', label: 'データ締切', group: 'data', requiresFeature: 'board_meeting', requiredPermission: { feature: 'tenant_config', action: 'view' } },
  // v0.5① 日報機能 増分1 (§14 P-5〜P-7): 3タブとも daily_report_config:view で表示、書込みは各画面が
  // requireTenantWideManage/isResourceInPermissionScope でバックエンド側に判定を委ねる (既存の
  // compensation/visibility-rules タブと同じ「タブ表示はview、実操作はページ内で出し分け」方針)。
  // 両edition対応 (§14-2表) のため fullOnly/liteOnly は付けない。daily_report は tenant_features に
  // 未登録のため requiredTenantFeature も付けない (:388 と同型)。
  { to: '/settings/daily-report/fields', label: '日報項目定義', group: 'data', requiredPermission: { feature: 'daily_report_config', action: 'view' } },
  { to: '/settings/daily-report/node-policies', label: '日報ノード方針', group: 'data', requiredPermission: { feature: 'daily_report_config', action: 'view' } },
  { to: '/settings/daily-report/reconciliation-rules', label: '日報突合ルール', group: 'data', requiredPermission: { feature: 'daily_report_config', action: 'view' } },
  { to: '/settings/daily-report/visibility-classes', label: '日報可視性クラス', group: 'data', requiredPermission: { feature: 'daily_report_config', action: 'view' } },
  { to: '/settings/employee-categories', label: '社員区分', group: 'hr' },
  { to: '/settings/employment-types', label: '雇用形態', group: 'hr' },
  // v0.4 章② (D-60): 36協定。Full 限定 (D-1)
  { to: '/settings/agreement-36', label: '36協定', group: 'hr', fullOnly: true, requiredPermission: { feature: 'agreement_36', action: 'view' } },
  // v0.4 章④ F-3 (§0-J-3、D-109/D-77): 必要人員設定 + シフトパターン・営業時間。Full 限定 (D-1)
  { to: '/settings/staffing', label: '必要人員設定', group: 'shift', fullOnly: true, requiredPermission: { feature: 'staffing', action: 'view' } },
  { to: '/settings/shift-patterns', label: 'シフトパターン・営業時間', group: 'shift', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'view' } },
  // v0.4 追加要件⑩ D-328 再設計 (D-333〜D-339): 調整不要な既定の勤務パターン (本社勤務者等)。
  // shift-patterns タブと同じ tenant_config:view 判定 (ノード別上書きを持たないため node 軸は無い)
  { to: '/settings/default-work-patterns', label: '既定勤務パターン', group: 'shift', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'view' } },
  // v0.4 追加要件⑫ 増分③ (D-379): 恒常勤務パターンの名前付きマスタ。shift-patterns タブと同じ
  // tenant_config:view 判定 (node_id は nullable のため tab 表示自体は feature+action のみで判定、
  // scope はバックエンドが最終防御する既存の運用に揃える)
  { to: '/settings/standing-pattern-templates', label: '恒常パターンテンプレート', group: 'shift', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'view' } },
  // v0.4 章⑤ F-4 (§0-J-3): 給与単価設定・可視性ルールは compensation:manage / tenant_config:manage の
  // all_stores 限定 (routes/compensation-settings.ts の requireTenantWideManage、GET も含む)。
  // 勤怠・法令設定はtenant_config:view (scope可変、shift-patternsタブと同じ運用)
  // ⚠️ TD-22: この2つは GET も含めて requireTenantWideManage (compensation-settings.ts:211/331/632)。
  // scope 盲目の hasPermission でタブを出すと、own_store の店長にはタブが見えるのに開くと
  // 一覧 GET 自体が 403 になる dead-end になる。
  { to: '/settings/compensation', label: '給与単価設定', group: 'hr', fullOnly: true, requiredPermission: { feature: 'compensation', action: 'manage' }, requiresTenantWideScope: true },
  { to: '/settings/visibility-rules', label: '可視性ルール', group: 'hr', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'manage' }, requiresTenantWideScope: true },
  { to: '/settings/attendance', label: '勤怠・法令設定', group: 'hr', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'view' } },
  // v0.4 追加要件⑳-3 (D-593〜D-596): 勤務実績の承認者設定。書込みはH-1/H-2により node_id の
  // 有無を問わず all_stores 限定だが、タブ自体は own_store/node_scope の閲覧者にも表示する
  // (GET は node_id 指定時に own_store でも閲覧できる、requiresTenantWideScope は付けない)。
  { to: '/settings/work-record-approvers', label: '勤務実績の承認者', group: 'hr', fullOnly: true, requiredPermission: { feature: 'tenant_config', action: 'view' } },
  // TD-28 段階③ (§10): 汎用申請ワークフロー・プラットフォームの申請種別マスタ。Full 限定 (D-23)
  { to: '/settings/request-types', label: '申請種別', group: 'hr', fullOnly: true, requiredPermission: { feature: 'request_type', action: 'view' } },
  // TD-28 D-24〜D-28: 申請項目の表示制御 (機密クラス × 表示コンテキスト × 承認レベル)
  { to: '/settings/request-field-visibility', label: '申請項目の表示制御', group: 'hr', fullOnly: true, requiredPermission: { feature: 'request_field_visibility', action: 'view' } },
  // v0.4 休暇管理 (TD-26) §6-1 L-3: 休暇種別マスタ + 法定値の確認 (D-279)。Full 限定 (D-1)。
  // ⚠️ requiresFeature='leave_management' を付ける理由: routes/leave-types.ts 自体は feature gate を
  //    持たないが、種別を登録しても leave_management が OFF なら付与・申請・残数のすべてが 403 で
  //    使えない (routes/leave.ts / leave-grants.ts が gate 済み)。設定だけ触れる状態を作らない。
  // ⚠️ requiredPermission は view: バックエンドの GET は leave_type:view (all/own/self)、
  //    書込みのみ manage:all_stores 限定 (D-281)。画面内で操作 UI を出し分ける。
  { to: '/settings/leave-types', label: '休暇種別', group: 'hr', fullOnly: true, requiresFeature: 'leave_management', requiredPermission: { feature: 'leave_type', action: 'view' } },
  // v0.5 ② タレントマネジメント階層構造化 (D-758/D-799): 業務・スキル・抽象度の階層設定 + ツリー編集。
  // 実測して確定した値 (docs/design/v05-competency-hierarchy-detail.md §5-1、6巡目 design-expert C-3 /
  // platform H-3): group は既存 'hr' の新規タブとして足す (新規 group を作ると ≤375px の Select 縮退の
  // 選択肢が増え、全設定画面の再検証が必要になるため)。
  // ⚠️ 工程⑬レビュー(design-expert Major)で訂正: requiredPermission は skill:manage ではなく
  // view にする (leave-types と同型パターン、上のコメント参照)。バックエンドの GET は skill:view
  // (どのスコープでも可) で通り、CompetencyHierarchySettingsPage.tsx 自体も !canManage で
  // 「閲覧のみ可能です」の読み取り専用UIを既に持つ。manage 限定のままだと view 権限者がタブ自体を
  // 発見できず、ページ側の読み取り専用モードが到達不能な dead UI になっていた。書込み系エンドポイント
  // (PUT系) は引き続きバックエンド側で requireTenantWideManage (skill:manage all_stores) を要求する。
  { to: '/settings/competency-hierarchy', label: '能力の階層', group: 'hr', fullOnly: true, requiresFeature: 'competency_hierarchy', requiredPermission: { feature: 'skill', action: 'view' } },
  // v0.4 追加要件⑪ 章⑦ (D-348/D-357): レポート設定 (表示項目・計算式)。定義は report_view:manage の
  // all_stores 限定 (compensation タブと同じ判断、テナント全体で共有される設定のため)
  { to: '/settings/report-views', label: 'レポート設定', group: 'shift', fullOnly: true, requiresFeature: 'report_formula_editor', requiredPermission: { feature: 'report_view', action: 'manage' } },
  { to: '/settings/features', label: '利用機能', group: 'system' },
  { to: '/settings/locale', label: '地域/通貨', group: 'system' },
  // v0.4 章③ (D-29/D-67/D-70): 用語辞書 + フィールド定義。
  // ⚠️ 章③レビュー時に修正 (design-and-code HIGH反映): 当初 requiredPermission に
  // tenant_config:view (章②の条件集合/階層継承タブと同じ運用) を付けていたが、
  // routes/labels.ts・routes/field-definitions.ts のバックエンドは isCompanyAdmin||isPlatformAdmin
  // のみで判定しておりカスタムロールの permission を一切見ない (D-70実装時の意図的な設計、
  // 会社管理者限定機能のため)。requiredPermission を残すと「tenant_config:view だけを持つ
  // カスタムロールユーザーにタブは見えるが開くと403」という不整合になるため外し、
  // 通常の isAdmin フォールバック (会社管理者以上) に統一してバックエンドの実際のゲートと揃える
  { to: '/settings/labels', label: '用語のカスタマイズ', group: 'system', requiresFeature: 'tenant_labels' },
  { to: '/settings/field-definitions', label: '入力項目の必須設定', group: 'system', requiresFeature: 'tenant_field_required' },
  // v0.3.x (AIトークン利用上限管理): Phase 1 (tenant_features.ai_usage_management=ON) かつ ai_usage:view 保有者のみ表示
  { to: '/settings/ai', label: 'AI設定', group: 'system', requiresFeature: 'ai_usage_management', requiredPermission: { feature: 'ai_usage', action: 'view' } },
  // v0.3.x (AIコメント カスタム指示): docs/design/v03-ai-comment-custom-instructions.md §8-1。
  // 両edition対応 (Full版の公開時自動生成・Lite版の手動生成の両方に効く) のため fullOnly/liteOnly は付けない
  // ラベルは「AI設定」(利用上限) と紛れないよう用途が分かる名前にする (Designレビュー M-15)
  { to: '/settings/ai-instructions', label: 'AIコメント指示', group: 'system', requiresFeature: 'ai_custom_instructions', requiredPermission: { feature: 'ai_instruction', action: 'view' } },
  // v0.4 §7 通知基盤 (L-1 in_app / L-2 email): チャネル有効/無効 + 配信タイミング設定。
  // 個人の購読ON/OFF・連絡先登録は /profile 側 (このタブは notification:manage 限定のため)
  { to: '/settings/notifications', label: '通知設定', group: 'system', requiresFeature: 'notification_platform', requiredPermission: { feature: 'notification', action: 'manage' } },
  // v0.4 章⑥ F-6 (§0-J-3): AIセットアップアシスタント。routes/ai-setup.ts の requireTenantWideManage
  // は ai_setup:manage の all_stores 限定だが、TABS の requiredPermission は他タブと同様 feature+action
  // のみ判定 (scope は画面内の実操作時にバックエンドが最終防御する既存の運用に揃える)
  { to: '/settings/ai-setup', label: 'AIセットアップ', group: 'system', fullOnly: true, requiredPermission: { feature: 'ai_setup', action: 'manage' } },
  // v0.4 章⑥ F-6 (§0-J-3、V-5): 下書き自動生成の割当ステージ設定
  { to: '/settings/assignment-stages', label: '下書き生成ステージ', group: 'shift', fullOnly: true, requiredPermission: { feature: 'shift_periods', action: 'manage' } },
];

export function SettingsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false;
  const permissions = user?.effective_permissions;
  const permsReady = usePermsReady();
  // v0.3 (S-16): role:manage等を持つカスタムロール保有者(company_admin以外)も
  // 該当タブにはアクセスできる必要がある (design doc §3.1「role:manageはカスタムロールにも付与可」)
  const hasAnyPermissionTab = permsReady && TABS.some((t) => t.requiredPermission && hasPermission(permissions, t.requiredPermission.feature, t.requiredPermission.action));
  const canAccessSettings = isAdmin || hasAnyPermissionTab;

  // ⚠️ 既知の制約: GET /tenant-features 自体がバックエンドでisCompanyAdmin限定のまま (v0.3スコープ外、
  // tenant-features.tsはS-15のCRUD粒度対応ファイル一覧に含まれない)。そのため company_admin ではない
  // role:manage 保有者 (「サブ管理者」等の委譲シナリオ) は custom_roles フラグの状態を取得できず、
  // 結果としてロール管理タブ自体が requiresFeature 判定で非表示になる。委譲シナリオは v0.4 以降で
  // tenant-features.ts 側の権限拡張を検討 (docs/design/v03-custom-roles.md 未記載、ここに申し送り)。
  const { data: features = [] } = useQuery<TenantFeature[]>({
    queryKey: ['tenant-features'],
    queryFn: () => apiFetch<TenantFeature[]>('/tenant-features'),
    enabled: canAccessSettings,
  });
  const enabledFeatureCodes = new Set(features.filter((f) => f.is_enabled).map((f) => f.feature_code));

  // isAdminはuser.rolesから同期的に判定できるため待つ必要がない。isAdminではない
  // ユーザーのみ、権限確定 (permsReady) まで許可/拒否の判定を保留しスケルトンを出す
  if (!isAdmin && !permsReady) {
    return (
      <div className="p-6 space-y-3" aria-busy="true" aria-label="読み込み中">
        <div className="h-4 w-40 rounded bg-surface-sunken animate-pulse" />
        <div className="h-24 rounded-lg bg-surface-sunken animate-pulse" />
      </div>
    );
  }

  if (!canAccessSettings) {
    return (
      <div className="p-6 border border-neutral-line rounded-lg bg-white text-neutral-sub">
        このページを表示する権限がありません (会社管理者以上が必要です)。
      </div>
    );
  }

  const visibleTabs = TABS.filter((t) => {
    if (t.fullOnly && user?.edition === 'lite') return false;
    if (t.liteOnly && user?.edition !== 'lite') return false;
    if (t.requiresFeature && !enabledFeatureCodes.has(t.requiresFeature)) return false;
    if (t.requiredPermission && !isAdmin) {
      const check = t.requiresTenantWideScope ? hasTenantWidePermission : hasPermission;
      if (!check(permissions, t.requiredPermission.feature, t.requiredPermission.action)) return false;
    }
    return true;
  });

  // v0.3 (S-0): グループ内にタブが1件も残らない (フィーチャーフラグ/権限で全滅) グループは
  // グループ選択タブ自体を出さない
  const visibleGroups = GROUP_ORDER.filter((g) => visibleTabs.some((t) => t.group === g));
  const activeGroup: SettingsGroup | undefined =
    visibleTabs.find((t) => location.pathname.startsWith(t.to))?.group ?? visibleGroups[0];
  const groupTabs = visibleTabs.filter((t) => t.group === activeGroup);

  return (
    <div className="space-y-4">
      <h1 className="text-h1">コンフィグ</h1>
      <div className="space-y-2">
        {/* v0.4 章③ (§3-6 Design N-1): グループ数が将来 4→6 (v0.4 章④/⑤の「シフト」「勤怠・給与」
            追加後) に増えるとピル並びが 375px 以下で発見性破綻する (v0.3 の「12タブ目でグループ化が
            必要になった」再発)。≤375px は Select (§8-101準拠、スクロール上限あり) へ縮退する */}
        <nav className="hidden min-[376px]:flex gap-1 overflow-x-auto" aria-label="設定グループ">
          {visibleGroups.map((g) => (
            <NavLink
              key={g}
              to={visibleTabs.find((t) => t.group === g)!.to}
              className={() =>
                `min-h-[44px] flex items-center px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition ${
                  g === activeGroup
                    ? 'bg-brand-red text-white'
                    : 'bg-surface-sunken text-neutral-sub hover:text-neutral-text'
                }`
              }
            >
              {GROUP_LABELS[g]}
            </NavLink>
          ))}
        </nav>
        <div className="min-[376px]:hidden">
          <Select
            value={activeGroup}
            onValueChange={(g) => navigate(visibleTabs.find((t) => t.group === g)!.to)}
          >
            <SelectTrigger aria-label="設定グループ" className="min-h-[44px]">
              <SelectValue>{activeGroup ? GROUP_LABELS[activeGroup] : ''}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {visibleGroups.map((g) => (
                <SelectItem key={g} value={g}>{GROUP_LABELS[g]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="border-b border-neutral-line overflow-x-auto">
          <nav className="flex gap-1 min-w-max" aria-label="設定タブ">
            {groupTabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={({ isActive }) =>
                  `min-h-[44px] flex items-center px-3 py-2 text-sm whitespace-nowrap border-b-2 transition ${
                    isActive
                      ? 'border-brand-red text-brand-red font-semibold'
                      : 'border-transparent text-neutral-sub hover:text-neutral-text hover:border-neutral-line'
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <div>
        <Outlet />
      </div>
    </div>
  );
}
